import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import ToolSEOContent from "@/components/ToolSEOContent";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import Header from "../components/Header";
import Footer from "../components/Footer";
import RelatedTools from "../components/RelatedTools";
import { toast } from "sonner";
import { 
  Youtube, Search, Hash, Type, FileText, Sparkles, 
  Video, Zap, TrendingUp, Copy, Check, RefreshCw,
  Target, BarChart2, Clock, Lightbulb, Film, Download, Image, Calendar,
  ChevronRight, Home
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || '';

const YouTubeSEOTools = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTool, setActiveTool] = useState("keyword-research");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Get tool from URL params
  useEffect(() => {
    const toolFromUrl = searchParams.get('tool');
    if (toolFromUrl) {
      setActiveTool(toolFromUrl);
    }
  }, [searchParams]);

  // Keyword Research State
  const [keyword, setKeyword] = useState("");
  const [keywordResults, setKeywordResults] = useState(null);

  // Tag Generator State
  const [videoTitle, setVideoTitle] = useState("");
  const [videoDescription, setVideoDescription] = useState("");
  const [tagResults, setTagResults] = useState(null);

  // Title Optimizer State
  const [titleToOptimize, setTitleToOptimize] = useState("");
  const [targetKeyword, setTargetKeyword] = useState("");
  const [titleResults, setTitleResults] = useState(null);

  // Description Generator State
  const [descVideoTitle, setDescVideoTitle] = useState("");
  const [descKeywords, setDescKeywords] = useState("");
  const [descResults, setDescResults] = useState(null);

  // Hashtag Generator State
  const [hashtagTopic, setHashtagTopic] = useState("");
  const [hashtagNiche, setHashtagNiche] = useState("");
  const [hashtagResults, setHashtagResults] = useState(null);

  // Script Generator State
  const [scriptTopic, setScriptTopic] = useState("");
  const [scriptType, setScriptType] = useState("long");
  const [scriptDuration, setScriptDuration] = useState(10);
  const [scriptResults, setScriptResults] = useState(null);

  // Hook Generator State
  const [hookTopic, setHookTopic] = useState("");
  const [hookResults, setHookResults] = useState(null);

  // Shorts Ideas State
  const [shortsNiche, setShortsNiche] = useState("");
  const [shortsResults, setShortsResults] = useState(null);

  // Thumbnail Text State
  const [thumbVideoTitle, setThumbVideoTitle] = useState("");
  const [thumbResults, setThumbResults] = useState(null);

  // Thumbnail Downloader State
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [thumbnailResults, setThumbnailResults] = useState(null);

  // Channel Audit State
  const [channelUrl, setChannelUrl] = useState("");
  const [channelAuditResults, setChannelAuditResults] = useState(null);

  // Competitor Analyzer State
  const [myChannelUrl, setMyChannelUrl] = useState("");
  const [competitorUrls, setCompetitorUrls] = useState("");
  const [competitorResults, setCompetitorResults] = useState(null);

  // Trending Topics State
  const [trendingNiche, setTrendingNiche] = useState("");
  const [trendingRegion, setTrendingRegion] = useState("US");
  const [trendingResults, setTrendingResults] = useState(null);

  // Monetization Checker State
  const [subscribers, setSubscribers] = useState("");
  const [watchHours, setWatchHours] = useState("");
  const [shortsViews, setShortsViews] = useState("");
  const [monetizationResults, setMonetizationResults] = useState(null);

  // Engagement Calculator State
  const [engViews, setEngViews] = useState("");
  const [engLikes, setEngLikes] = useState("");
  const [engComments, setEngComments] = useState("");
  const [engagementResults, setEngagementResults] = useState(null);

  // Rank Tracker State
  const [rankKeywords, setRankKeywords] = useState("");
  const [rankRegion, setRankRegion] = useState("US");
  const [rankVideoUrl, setRankVideoUrl] = useState("");
  const [rankResults, setRankResults] = useState(null);

  // Best Upload Time State
  const [uploadAudience, setUploadAudience] = useState("");
  const [uploadTimezone, setUploadTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  });
  const [uploadResults, setUploadResults] = useState(null);

  // CTR Estimator State
  const [ctrTitle, setCtrTitle] = useState("");
  const [ctrThumbnailText, setCtrThumbnailText] = useState("");
  const [ctrNiche, setCtrNiche] = useState("");
  const [ctrImpressions, setCtrImpressions] = useState("");
  const [ctrResults, setCtrResults] = useState(null);

  // Track My Channel State
  const [trackedChannel, setTrackedChannel] = useState(null);
  const [trackChannelLoading, setTrackChannelLoading] = useState(false);
  const [trackChannelUrl, setTrackChannelUrl] = useState("");
  const [channelHistory, setChannelHistory] = useState([]);

  // Video Performance Predictor State
  const [predictorTitle, setPredictorTitle] = useState("");
  const [predictorThumbnailText, setPredictorThumbnailText] = useState("");
  const [predictorNiche, setPredictorNiche] = useState("");
  const [predictorDuration, setPredictorDuration] = useState("10");
  const [predictorResults, setPredictorResults] = useState(null);
  const [loadingPredictor, setLoadingPredictor] = useState(false);

  // A/B Title Testing State
  const [abTitleA, setAbTitleA] = useState("");
  const [abTitleB, setAbTitleB] = useState("");
  const [abNiche, setAbNiche] = useState("");
  const [abResults, setAbResults] = useState(null);
  const [loadingAB, setLoadingAB] = useState(false);

  // A/B Title Testing Handler
  const handleABTitleTest = () => {
    if (!abTitleA.trim() || !abTitleB.trim()) {
      return;
    }

    setLoadingAB(true);
    
    const analyzeTitle = (title) => {
      const length = title.length;
      const hasEmoji = /[\u{1F300}-\u{1F9FF}]/u.test(title);
      const hasNumbers = /\d/.test(title);
      const hasQuestion = title.includes('?');
      const hasPowerWords = /best|top|ultimate|secret|hack|proven|amazing|shocking|incredible/i.test(title);
      const hasBrackets = /\[|\(/.test(title);
      
      let score = 50;
      if (length >= 40 && length <= 60) score += 15;
      else if (length < 30) score -= 10;
      else if (length > 70) score -= 15;
      
      if (hasEmoji) score += 5;
      if (hasNumbers) score += 10;
      if (hasQuestion) score += 8;
      if (hasPowerWords) score += 12;
      if (hasBrackets) score += 5;
      
      return {
        score: Math.min(100, Math.max(0, score)),
        length,
        hasEmoji,
        hasNumbers,
        hasQuestion,
        hasPowerWords,
        hasBrackets,
        ctrEstimate: (2 + (score / 100) * 8).toFixed(1)
      };
    };

    setTimeout(() => {
      const analysisA = analyzeTitle(abTitleA);
      const analysisB = analyzeTitle(abTitleB);
      
      setAbResults({
        titleA: { title: abTitleA, ...analysisA },
        titleB: { title: abTitleB, ...analysisB },
        winner: analysisA.score > analysisB.score ? 'A' : analysisB.score > analysisA.score ? 'B' : 'Tie',
        recommendation: analysisA.score > analysisB.score 
          ? `Title A is predicted to perform ${Math.round(analysisA.score - analysisB.score)}% better`
          : analysisB.score > analysisA.score
          ? `Title B is predicted to perform ${Math.round(analysisB.score - analysisA.score)}% better`
          : "Both titles are expected to perform similarly"
      });
      setLoadingAB(false);
    }, 800);
  };

  // Shorts Scripts State
  const [shortsScriptNiche, setShortsScriptNiche] = useState("");
  const [generatedShortsScripts, setGeneratedShortsScripts] = useState(null);
  const [loadingShortsScripts, setLoadingShortsScripts] = useState(false);

  // Video Calendar State
  const [calendarNiche, setCalendarNiche] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [generatedCalendar, setGeneratedCalendar] = useState(null);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  // Shorts Scripts Handler
  const handleGenerateShortsScripts = () => {
    if (!shortsScriptNiche.trim()) {
      toast.error("Please enter your niche");
      return;
    }
    setLoadingShortsScripts(true);
    
    setTimeout(() => {
      const scripts = [
        {
          title: `3 ${shortsScriptNiche} Tips You NEED to Know`,
          hook: "Stop scrolling! Here's something that'll change your game...",
          body: "Tip 1: [Quick actionable advice]\nTip 2: [Another valuable insight]\nTip 3: [Game-changing technique]",
          cta: "Follow for more ${shortsScriptNiche} tips!",
          duration: "15-30 sec"
        },
        {
          title: `POV: You finally mastered ${shortsScriptNiche}`,
          hook: "This is what success looks like...",
          body: "[Show transformation or result]\n[Reveal the secret]\n[Quick how-to]",
          cta: "Want the full guide? Link in bio!",
          duration: "15-20 sec"
        },
        {
          title: `${shortsScriptNiche} mistake everyone makes`,
          hook: "I see this ALL the time and it needs to stop...",
          body: "[Point out common mistake]\n[Explain why it's wrong]\n[Show the correct way]",
          cta: "Save this so you don't forget!",
          duration: "20-30 sec"
        },
        {
          title: `Day in my life as a ${shortsScriptNiche} creator`,
          hook: "Let me show you what my day looks like...",
          body: "[Morning routine snippet]\n[Work/Create moment]\n[Results or progress]",
          cta: "Would you want this lifestyle?",
          duration: "30-45 sec"
        }
      ];
      
      setGeneratedShortsScripts({ niche: shortsScriptNiche, scripts });
      setLoadingShortsScripts(false);
      toast.success("Scripts generated!");
    }, 600);
  };

  // Video Calendar Handler
  const handleGenerateCalendar = () => {
    if (!calendarNiche.trim()) {
      toast.error("Please enter your niche");
      return;
    }
    setLoadingCalendar(true);
    
    setTimeout(() => {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const ideas = [];
      const templates = [
        `How to Get Started with ${calendarNiche}`,
        `${calendarNiche} Mistakes to Avoid`,
        `My ${calendarNiche} Journey (Story Time)`,
        `${calendarNiche} Tools I Use Daily`,
        `Beginner vs Pro ${calendarNiche}`,
        `${calendarNiche} Tips That Changed My Life`,
        `Reacting to ${calendarNiche} Content`,
        `${calendarNiche} Q&A - Your Questions Answered`,
        `Day in My Life: ${calendarNiche} Edition`,
        `${calendarNiche} Trends in 2024`,
        `Behind the Scenes: ${calendarNiche}`,
        `${calendarNiche} Challenge Results`
      ];
      
      // Generate 4 video ideas per week (16 per month)
      for (let week = 1; week <= 4; week++) {
        for (let day = 0; day < 4; day++) {
          const idx = ((week - 1) * 4 + day) % templates.length;
          ideas.push({
            week,
            day: ['Mon', 'Wed', 'Fri', 'Sat'][day],
            title: templates[idx],
            type: day === 0 ? 'Long-form' : day === 3 ? 'Shorts' : 'Standard'
          });
        }
      }
      
      setGeneratedCalendar({
        niche: calendarNiche,
        month: months[calendarMonth],
        ideas
      });
      setLoadingCalendar(false);
      toast.success("Calendar generated!");
    }, 700);
  };

  // Video Performance Prediction
  const handleVideoPredictor = async () => {
    if (!predictorTitle.trim()) {
      return;
    }
    
    setLoadingPredictor(true);
    try {
      // Calculate prediction based on title analysis
      const titleLength = predictorTitle.length;
      const hasEmoji = /[\u{1F300}-\u{1F9FF}]/u.test(predictorTitle);
      const hasNumbers = /\d/.test(predictorTitle);
      const hasQuestion = predictorTitle.includes('?');
      const thumbnailHasText = predictorThumbnailText.trim().length > 0;
      
      // Calculate scores
      let ctrScore = 5;
      if (titleLength >= 40 && titleLength <= 60) ctrScore += 1.5;
      if (hasEmoji) ctrScore += 0.5;
      if (hasNumbers) ctrScore += 1;
      if (hasQuestion) ctrScore += 0.5;
      if (thumbnailHasText && predictorThumbnailText.length <= 30) ctrScore += 1;
      
      // Deterministic — no noise, scores reflect the input exactly.
      ctrScore = Math.min(10, ctrScore);

      const viewPotential = Math.round(ctrScore * 1000);
      const engagementScore = Math.min(10, ctrScore * 0.8);
      
      const prediction = {
        overall_score: Math.round(ctrScore * 10),
        ctr_estimate: (ctrScore * 0.8).toFixed(1),
        view_potential: viewPotential.toLocaleString() + '+',
        engagement_score: engagementScore.toFixed(1),
        viral_probability: Math.round(Math.min(100, ctrScore * 8)),
        title_analysis: {
          length_score: titleLength >= 40 && titleLength <= 60 ? 'Optimal' : titleLength < 40 ? 'Too Short' : 'Too Long',
          power_words: hasQuestion || predictorTitle.toLowerCase().includes('how') || predictorTitle.toLowerCase().includes('why'),
          curiosity_gap: hasQuestion || predictorTitle.includes('...'),
          number_usage: hasNumbers
        },
        thumbnail_analysis: {
          has_text: thumbnailHasText,
          text_length: thumbnailHasText ? (predictorThumbnailText.length <= 30 ? 'Good' : 'Too Long') : 'No text'
        },
        recommendations: [
          titleLength < 40 ? 'Make your title longer (40-60 chars optimal)' : null,
          titleLength > 70 ? 'Shorten your title for better mobile display' : null,
          !hasNumbers ? 'Add numbers to increase CTR (e.g., "5 Tips", "2024")' : null,
          !hasQuestion ? 'Try phrasing as a question to spark curiosity' : null,
          !thumbnailHasText ? 'Add 2-4 words of text to your thumbnail' : null,
          predictorThumbnailText.length > 30 ? 'Reduce thumbnail text to 3-4 words max' : null
        ].filter(Boolean),
        best_upload_times: ['9:00 AM', '12:00 PM', '5:00 PM', '8:00 PM'],
        suggested_tags: ['viral', predictorNiche || 'trending', 'fyp', '2024', 'howto'].slice(0, 5)
      };
      
      setPredictorResults(prediction);
    } catch (error) {
      console.error("Prediction failed:", error);
    } finally {
      setLoadingPredictor(false);
    }
  };

  // Handle Track My Channel
  const handleTrackChannel = async () => {
    if (!trackChannelUrl.trim()) {
      return;
    }
    setTrackChannelLoading(true);
    try {
      // Use channel audit endpoint for data
      const response = await axios.post(`${API}/api/youtube/channel-audit`, {
        channel_url: trackChannelUrl
      });
      
      // Store the tracked channel data
      const channelData = {
        ...response.data,
        trackedAt: new Date().toISOString(),
        channelUrl: trackChannelUrl
      };
      
      setTrackedChannel(channelData);
      
      // Add to history (keep last 5)
      setChannelHistory(prev => [channelData, ...prev.slice(0, 4)]);
      
      // Store in localStorage for persistence
      localStorage.setItem('trackedChannel', JSON.stringify(channelData));
      
    } catch (error) {
      console.error("Channel tracking failed:", error);
    }
    setTrackChannelLoading(false);
  };

  // Load tracked channel from localStorage on mount
  React.useEffect(() => {
    const savedChannel = localStorage.getItem('trackedChannel');
    if (savedChannel) {
      try {
        setTrackedChannel(JSON.parse(savedChannel));
      } catch (e) {}
    }
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tools = [
    { id: "track-my-channel", name: "Track My Channel", icon: BarChart2, desc: "Channel analytics dashboard", highlight: true },
    { id: "video-predictor", name: "Video Predictor", icon: Sparkles, desc: "Predict video performance", badge: "AI" },
    { id: "keyword-research", name: "Keyword Research", icon: Search, desc: "Find high-volume keywords" },
    { id: "rank-tracker", name: "Rank Tracker", icon: BarChart2, desc: "Track keyword ranks" },
    { id: "tag-generator", name: "Tag Generator", icon: Hash, desc: "Generate ranking tags" },
    { id: "title-optimizer", name: "Title Optimizer", icon: Type, desc: "Boost your CTR" },
    { id: "ab-title-test", name: "A/B Title Testing", icon: Target, desc: "Compare two titles", badge: "NEW" },
    { id: "ctr-estimator", name: "CTR Estimator", icon: Target, desc: "Estimate click rate" },
    { id: "description-generator", name: "Description Generator", icon: FileText, desc: "SEO descriptions" },
    { id: "hashtag-generator", name: "Hashtag Generator", icon: TrendingUp, desc: "Trending hashtags" },
    { id: "script-generator", name: "Script Generator", icon: Video, desc: "Video scripts" },
    { id: "hook-generator", name: "Hook Generator", icon: Zap, desc: "Viral intros" },
    { id: "shorts-ideas", name: "Shorts Ideas", icon: Film, desc: "Viral shorts concepts" },
    { id: "shorts-scripts", name: "Shorts Scripts", icon: FileText, desc: "Ready-made short scripts", badge: "NEW" },
    { id: "video-calendar", name: "Video Calendar", icon: Calendar, desc: "Monthly video ideas", badge: "NEW" },
    { id: "thumbnail-text", name: "Thumbnail Text", icon: Lightbulb, desc: "CTR-boosting text" },
    { id: "thumbnail-downloader", name: "Thumbnail Downloader", icon: Download, desc: "Download thumbnails" },
    { id: "channel-audit", name: "Channel Audit", icon: BarChart2, desc: "Full channel analysis" },
    { id: "competitor-analyzer", name: "Competitor Analyzer", icon: Target, desc: "Spy on competitors" },
    { id: "trending-topics", name: "Trending Topics", icon: TrendingUp, desc: "What's trending now" },
    { id: "best-upload-time", name: "Best Upload Time", icon: Calendar, desc: "Ideal upload schedule" },
    { id: "monetization-checker", name: "Monetization", icon: Clock, desc: "YPP eligibility" },
    { id: "engagement-calculator", name: "Engagement Calc", icon: BarChart2, desc: "Analyze engagement" },
  ];

  // API Handlers
  const handleKeywordResearch = async () => {
    if (!keyword.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/keyword-research`, { keyword });
      setKeywordResults(response.data);
    } catch (error) {
      console.error("Keyword research failed:", error);
    }
    setLoading(false);
  };

  const handleTagGenerator = async () => {
    if (!videoTitle.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/tag-generator`, {
        video_title: videoTitle,
        video_description: videoDescription,
        count: 30
      });
      setTagResults(response.data);
    } catch (error) {
      console.error("Tag generation failed:", error);
    }
    setLoading(false);
  };

  const handleTitleOptimizer = async () => {
    if (!titleToOptimize.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/title-optimizer`, {
        title: titleToOptimize,
        target_keyword: targetKeyword || null
      });
      setTitleResults(response.data);
    } catch (error) {
      console.error("Title optimization failed:", error);
    }
    setLoading(false);
  };

  const handleDescriptionGenerator = async () => {
    if (!descVideoTitle.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/description-generator`, {
        video_title: descVideoTitle,
        keywords: descKeywords ? descKeywords.split(",").map(k => k.trim()) : [],
        include_timestamps: true,
        include_hashtags: true,
        include_cta: true
      });
      setDescResults(response.data);
    } catch (error) {
      console.error("Description generation failed:", error);
    }
    setLoading(false);
  };

  const handleHashtagGenerator = async () => {
    if (!hashtagTopic.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/hashtag-generator`, {
        topic: hashtagTopic,
        niche: hashtagNiche || null,
        count: 15
      });
      setHashtagResults(response.data);
    } catch (error) {
      console.error("Hashtag generation failed:", error);
    }
    setLoading(false);
  };

  const handleScriptGenerator = async () => {
    if (!scriptTopic.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/script-generator`, {
        topic: scriptTopic,
        script_type: scriptType,
        duration_minutes: scriptDuration,
        tone: "engaging"
      });
      setScriptResults(response.data);
    } catch (error) {
      console.error("Script generation failed:", error);
    }
    setLoading(false);
  };

  const handleHookGenerator = async () => {
    if (!hookTopic.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/hook-generator`, {
        video_topic: hookTopic,
        count: 5
      });
      setHookResults(response.data);
    } catch (error) {
      console.error("Hook generation failed:", error);
    }
    setLoading(false);
  };

  const handleShortsIdeas = async () => {
    if (!shortsNiche.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/shorts-ideas`, {
        niche: shortsNiche,
        count: 10
      });
      setShortsResults(response.data);
    } catch (error) {
      console.error("Shorts ideas generation failed:", error);
    }
    setLoading(false);
  };

  const handleThumbnailText = async () => {
    if (!thumbVideoTitle.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/thumbnail-text`, {
        video_title: thumbVideoTitle,
        count: 5
      });
      setThumbResults(response.data);
    } catch (error) {
      console.error("Thumbnail text generation failed:", error);
    }
    setLoading(false);
  };

  // Extract Video ID from YouTube URL
  const extractVideoId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleThumbnailDownloader = () => {
    const videoId = extractVideoId(thumbnailUrl.trim());
    if (!videoId) {
      setThumbnailResults({ error: "Invalid YouTube URL. Please enter a valid YouTube video link." });
      return;
    }

    const thumbnails = [
      { quality: "Max Resolution (1280x720)", size: "maxresdefault", url: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` },
      { quality: "High Quality (480x360)", size: "hqdefault", url: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` },
      { quality: "Medium Quality (320x180)", size: "mqdefault", url: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` },
      { quality: "Standard (640x480)", size: "sddefault", url: `https://img.youtube.com/vi/${videoId}/sddefault.jpg` },
      { quality: "Default (120x90)", size: "default", url: `https://img.youtube.com/vi/${videoId}/default.jpg` },
    ];

    setThumbnailResults({
      videoId,
      thumbnails,
      videoUrl: `https://youtube.com/watch?v=${videoId}`
    });
  };

  const downloadThumbnail = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // Fallback: open in new tab
      window.open(url, '_blank');
    }
  };

  // Phase 2 Handlers
  const handleChannelAudit = async () => {
    if (!channelUrl.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/channel-audit`, {
        channel_url: channelUrl
      });
      setChannelAuditResults(response.data);
    } catch (error) {
      console.error("Channel audit failed:", error);
    }
    setLoading(false);
  };

  const handleCompetitorAnalysis = async () => {
    if (!myChannelUrl.trim()) return;
    setLoading(true);
    try {
      const competitors = competitorUrls.split('\n').filter(u => u.trim());
      const response = await axios.post(`${API}/api/youtube/competitor-analyzer`, {
        channel_url: myChannelUrl,
        competitor_urls: competitors.length > 0 ? competitors : null
      });
      setCompetitorResults(response.data);
    } catch (error) {
      console.error("Competitor analysis failed:", error);
    }
    setLoading(false);
  };

  const handleTrendingTopics = async () => {
    if (!trendingNiche.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/trending-topics`, {
        niche: trendingNiche,
        region: trendingRegion,
        count: 10
      });
      setTrendingResults(response.data);
    } catch (error) {
      console.error("Trending topics failed:", error);
    }
    setLoading(false);
  };

  const handleMonetizationCheck = async () => {
    if (!subscribers || !watchHours) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/monetization-checker`, {
        subscribers: parseInt(subscribers) || 0,
        watch_hours: parseInt(watchHours) || 0,
        shorts_views: parseInt(shortsViews) || 0
      });
      setMonetizationResults(response.data);
    } catch (error) {
      console.error("Monetization check failed:", error);
    }
    setLoading(false);
  };

  const handleEngagementCalculator = async () => {
    if (!engViews || !engLikes) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/engagement-calculator`, {
        views: parseInt(engViews) || 0,
        likes: parseInt(engLikes) || 0,
        comments: parseInt(engComments) || 0
      });
      setEngagementResults(response.data);
    } catch (error) {
      console.error("Engagement calculation failed:", error);
    }
    setLoading(false);
  };

  const handleRankTracker = async () => {
    const keywords = rankKeywords
      .split(/[\n,]/)
      .map((k) => k.trim())
      .filter((k) => k);
    if (keywords.length === 0) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/rank-tracker`, {
        keywords,
        region: rankRegion,
        video_url: rankVideoUrl || null
      });
      setRankResults(response.data);
    } catch (error) {
      console.error("Rank tracker failed:", error);
    }
    setLoading(false);
  };

  const handleBestUploadTime = async () => {
    if (!uploadAudience.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/best-upload-time`, {
        target_audience: uploadAudience,
        timezone: uploadTimezone || "UTC"
      });
      setUploadResults(response.data);
    } catch (error) {
      console.error("Upload time analysis failed:", error);
    }
    setLoading(false);
  };

  const handleCtrEstimator = async () => {
    if (!ctrTitle.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/youtube/ctr-estimator`, {
        title: ctrTitle,
        thumbnail_text: ctrThumbnailText || null,
        niche: ctrNiche || null,
        impressions: ctrImpressions ? parseInt(ctrImpressions) : null
      });
      setCtrResults(response.data);
    } catch (error) {
      console.error("CTR estimation failed:", error);
    }
    setLoading(false);
  };

  const normalizeUploadTimes = (times) => {
    if (!times) return [];
    if (Array.isArray(times)) {
      return times.map((time) => ({ label: time }));
    }
    return Object.entries(times).flatMap(([day, list]) =>
      (Array.isArray(list) ? list : [list]).map((time) => ({ label: `${day}: ${time}` }))
    );
  };

  const formatReasoning = (reasoning) => {
    if (!reasoning) return "";
    if (typeof reasoning === "string") return reasoning;
    if (Array.isArray(reasoning)) return reasoning.join(" ");
    return Object.entries(reasoning)
      .map(([key, value]) => {
        const label = key.replace(/_/g, " ");
        const text = Array.isArray(value) ? value.join(", ") : value;
        return `${label}: ${text}`;
      })
      .join(" | ");
  };

  const renderToolContent = () => {
    switch (activeTool) {
      case "track-my-channel":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center">
                <BarChart2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Track My Channel</h2>
                <p className="text-gray-600">Monitor your YouTube channel's performance</p>
              </div>
            </div>

            {/* Channel Input */}
            <div className="flex gap-3">
              <Input
                placeholder="Enter your YouTube Channel URL"
                value={trackChannelUrl}
                onChange={(e) => setTrackChannelUrl(e.target.value)}
                className="flex-1"
                data-testid="track-channel-url-input"
              />
              <Button 
                onClick={handleTrackChannel}
                disabled={trackChannelLoading}
                className="bg-red-600 hover:bg-red-700"
                data-testid="track-channel-btn"
              >
                {trackChannelLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                ) : (
                  <><BarChart2 className="w-4 h-4 mr-2" /> Track Channel</>
                )}
              </Button>
            </div>

            {/* Channel Dashboard */}
            {trackedChannel ? (
              <div className="space-y-6">
                {/* Channel Header */}
                <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-xl border border-red-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {trackedChannel.channel_info?.name || "Your Channel"}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Last updated: {new Date(trackedChannel.trackedAt).toLocaleString()}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleTrackChannel} disabled={trackChannelLoading}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${trackChannelLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-xl border shadow-sm">
                    <p className="text-sm text-gray-500 mb-1">Subscribers</p>
                    <p className="text-2xl font-bold text-red-600">
                      {trackedChannel.channel_info?.subscribers?.toLocaleString() || "N/A"}
                    </p>
                    <p className="text-xs text-green-600">▲ Growing</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border shadow-sm">
                    <p className="text-sm text-gray-500 mb-1">Total Videos</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {trackedChannel.channel_info?.total_videos?.toLocaleString() || "N/A"}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border shadow-sm">
                    <p className="text-sm text-gray-500 mb-1">Total Views</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {trackedChannel.channel_info?.total_views?.toLocaleString() || "N/A"}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-xl border shadow-sm">
                    <p className="text-sm text-gray-500 mb-1">Health Score</p>
                    <p className="text-2xl font-bold text-green-600">
                      {trackedChannel.overall_health_score || "N/A"}/100
                    </p>
                  </div>
                </div>

                {/* SEO & Performance Analysis */}
                {trackedChannel.seo_analysis && (
                  <div className="bg-white p-6 rounded-xl border">
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Search className="w-5 h-5 text-red-600" />
                      SEO Analysis
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Title Score</p>
                        <p className="text-lg font-semibold">{trackedChannel.seo_analysis.title_optimization?.score || "N/A"}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Description Score</p>
                        <p className="text-lg font-semibold">{trackedChannel.seo_analysis.description_quality?.score || "N/A"}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Keywords Usage</p>
                        <p className="text-lg font-semibold">{trackedChannel.seo_analysis.keyword_usage?.score || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {trackedChannel.recommendations && trackedChannel.recommendations.length > 0 && (
                  <div className="bg-white p-6 rounded-xl border">
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-yellow-500" />
                      Recommendations
                    </h4>
                    <div className="space-y-3">
                      {trackedChannel.recommendations.slice(0, 5).map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                            ${rec.priority === 'high' ? 'bg-red-500 text-white' : 
                              rec.priority === 'medium' ? 'bg-yellow-500 text-white' : 'bg-gray-300'}`}>
                            {idx + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{rec.title}</p>
                            <p className="text-sm text-gray-600">{rec.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Content Performance */}
                {trackedChannel.content_analysis && (
                  <div className="bg-white p-6 rounded-xl border">
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      Content Performance
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-sm text-gray-600">Upload Frequency</p>
                        <p className="font-semibold text-green-700">
                          {trackedChannel.content_analysis.upload_frequency || "N/A"}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-gray-600">Avg. Views</p>
                        <p className="font-semibold text-blue-700">
                          {trackedChannel.content_analysis.average_views?.toLocaleString() || "N/A"}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <p className="text-sm text-gray-600">Engagement Rate</p>
                        <p className="font-semibold text-purple-700">
                          {trackedChannel.content_analysis.engagement_rate || "N/A"}%
                        </p>
                      </div>
                      <div className="text-center p-3 bg-orange-50 rounded-lg">
                        <p className="text-sm text-gray-600">Content Consistency</p>
                        <p className="font-semibold text-orange-700">
                          {trackedChannel.content_analysis.content_consistency || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed">
                <Youtube className="w-16 h-16 text-red-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">No Channel Tracked</h3>
                <p className="text-gray-500 mb-4">Enter your YouTube channel URL above to start tracking your performance</p>
                <p className="text-sm text-gray-400">Your channel data will be saved locally for quick access</p>
              </div>
            )}
          </div>
        );

      case "video-predictor":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Video Performance Predictor</h2>
                <p className="text-gray-600">AI-powered video success prediction</p>
              </div>
              <Badge className="ml-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white">AI</Badge>
            </div>

            {/* Input Form */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Video Title *</label>
                <Input
                  placeholder="e.g., 10 SEO Tips That Actually Work in 2024"
                  value={predictorTitle}
                  onChange={(e) => setPredictorTitle(e.target.value)}
                  data-testid="predictor-title-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Thumbnail Text</label>
                <Input
                  placeholder="e.g., SEO SECRETS"
                  value={predictorThumbnailText}
                  onChange={(e) => setPredictorThumbnailText(e.target.value)}
                  data-testid="predictor-thumbnail-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Niche/Category</label>
                <Input
                  placeholder="e.g., Tech, Gaming, Education"
                  value={predictorNiche}
                  onChange={(e) => setPredictorNiche(e.target.value)}
                  data-testid="predictor-niche-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Video Duration (mins)</label>
                <Input
                  type="number"
                  placeholder="10"
                  value={predictorDuration}
                  onChange={(e) => setPredictorDuration(e.target.value)}
                  data-testid="predictor-duration-input"
                />
              </div>
            </div>

            <Button
              onClick={handleVideoPredictor}
              disabled={loadingPredictor || !predictorTitle.trim()}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              data-testid="predict-btn"
            >
              {loadingPredictor ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Predict Performance</>
              )}
            </Button>

            {/* Results */}
            {predictorResults && (
              <div className="space-y-6">
                {/* Overall Score */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Performance Prediction</h3>
                    <div className="text-center">
                      <div className={`text-4xl font-bold ${
                        predictorResults.overall_score >= 70 ? 'text-green-600' :
                        predictorResults.overall_score >= 50 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {predictorResults.overall_score}/100
                      </div>
                      <p className="text-xs text-gray-500">Overall Score</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">{predictorResults.ctr_estimate}%</p>
                      <p className="text-xs text-gray-500">Est. CTR</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">{predictorResults.view_potential}</p>
                      <p className="text-xs text-gray-500">View Potential</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{predictorResults.engagement_score}/10</p>
                      <p className="text-xs text-gray-500">Engagement</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-pink-600">{predictorResults.viral_probability}%</p>
                      <p className="text-xs text-gray-500">Viral Chance</p>
                    </div>
                  </div>
                </div>

                {/* Analysis Details */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-xl border">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Type className="w-4 h-4 text-purple-600" />
                      Title Analysis
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Length</span>
                        <Badge className={predictorResults.title_analysis.length_score === 'Optimal' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                          {predictorResults.title_analysis.length_score}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Power Words</span>
                        <Badge className={predictorResults.title_analysis.power_words ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                          {predictorResults.title_analysis.power_words ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Numbers</span>
                        <Badge className={predictorResults.title_analysis.number_usage ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                          {predictorResults.title_analysis.number_usage ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl border">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      Best Upload Times
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {predictorResults.best_upload_times.map((time, idx) => (
                        <Badge key={idx} variant="outline">{time}</Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                {predictorResults.recommendations.length > 0 && (
                  <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                    <h4 className="font-semibold mb-3 flex items-center gap-2 text-yellow-800">
                      <Lightbulb className="w-4 h-4" />
                      Recommendations to Improve
                    </h4>
                    <ul className="space-y-2">
                      {predictorResults.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-yellow-700">
                          <span className="w-5 h-5 bg-yellow-200 rounded-full flex items-center justify-center text-xs font-bold shrink-0">
                            {idx + 1}
                          </span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Suggested Tags */}
                <div className="bg-white p-4 rounded-xl border">
                  <h4 className="font-semibold mb-3">Suggested Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {predictorResults.suggested_tags.map((tag, idx) => (
                      <Badge key={idx} className="bg-purple-100 text-purple-700">#{tag}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!predictorResults && !loadingPredictor && (
              <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed">
                <Sparkles className="w-12 h-12 text-purple-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">Predict Your Video's Success</h3>
                <p className="text-gray-500">Enter your video details above to get AI-powered performance predictions</p>
              </div>
            )}
          </div>
        );

      case "keyword-research":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <Search className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">YouTube Keyword Research</h2>
                <p className="text-gray-600">Find high-volume, low-competition keywords</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Input
                placeholder="Enter seed keyword (e.g., 'how to edit videos')"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                className="flex-1"
                data-testid="yt-keyword-input"
              />
              <Button 
                onClick={handleKeywordResearch}
                disabled={loading || !keyword.trim()}
                className="bg-red-600 hover:bg-red-700"
                data-testid="yt-keyword-search-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Research
              </Button>
            </div>

            {keywordResults && (
              <div className="space-y-4">
                <Card className="p-4 bg-gradient-to-r from-red-50 to-orange-50">
                  <h3 className="font-semibold text-gray-900 mb-3">Main Keyword: {keywordResults.seed_keyword}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Search Volume</p>
                      <p className="text-xl font-bold text-red-600">{keywordResults.main_metrics?.search_volume?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Competition</p>
                      <p className="text-xl font-bold text-orange-600">{keywordResults.main_metrics?.competition_label}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Trend</p>
                      <p className="text-xl font-bold text-green-600">{keywordResults.main_metrics?.trend}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Keywords</p>
                      <p className="text-xl font-bold text-violet-600">{keywordResults.total_keywords}</p>
                    </div>
                  </div>
                </Card>

                <div className="bg-white rounded-lg border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Keyword</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Volume</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Competition</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Intent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keywordResults.suggestions?.map((kw, i) => (
                        <tr key={i} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{kw.keyword}</td>
                          <td className="px-4 py-3 text-sm">{kw.search_volume?.toLocaleString()}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              kw.competition < 30 ? 'bg-green-100 text-green-700' :
                              kw.competition < 60 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {kw.competition_label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{kw.intent}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case "rank-tracker":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <BarChart2 className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">YouTube Rank Tracker</h2>
                <p className="text-gray-600">Track your keyword positions and trends</p>
              </div>
            </div>

            <div className="space-y-3">
              <Textarea
                placeholder="Enter keywords (comma or new line separated)"
                value={rankKeywords}
                onChange={(e) => setRankKeywords(e.target.value)}
                rows={4}
                data-testid="yt-rank-keywords-input"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  placeholder="Region (e.g., US, IN, UK)"
                  value={rankRegion}
                  onChange={(e) => setRankRegion(e.target.value)}
                  data-testid="yt-rank-region-input"
                />
                <Input
                  placeholder="Video URL (optional)"
                  value={rankVideoUrl}
                  onChange={(e) => setRankVideoUrl(e.target.value)}
                  data-testid="yt-rank-video-input"
                />
              </div>
              <Button
                onClick={handleRankTracker}
                disabled={loading || !rankKeywords.trim()}
                className="w-full bg-slate-700 hover:bg-slate-800"
                data-testid="yt-rank-track-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <BarChart2 className="w-4 h-4 mr-2" />}
                Track Rankings
              </Button>
            </div>

            {rankResults && (
              <div className="space-y-4">
                <Card className="p-4 bg-slate-50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Avg Position</p>
                      <p className="text-2xl font-bold text-slate-700" data-testid="yt-rank-avg">
                        {rankResults.average_position}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Top 3</p>
                      <p className="text-2xl font-bold text-green-600" data-testid="yt-rank-top3">
                        {rankResults.summary?.top_3}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Top 10</p>
                      <p className="text-2xl font-bold text-blue-600" data-testid="yt-rank-top10">
                        {rankResults.summary?.top_10}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Top 20</p>
                      <p className="text-2xl font-bold text-purple-600" data-testid="yt-rank-top20">
                        {rankResults.summary?.top_20}
                      </p>
                    </div>
                  </div>
                </Card>

                <div className="bg-white rounded-lg border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Keyword</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Position</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Trend</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Competition</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankResults.results?.map((item, i) => (
                        <tr key={i} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{item.keyword}</td>
                          <td className="px-4 py-3 text-sm font-medium">#{item.position}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              item.trend === "up" ? "bg-green-100 text-green-700" :
                              item.trend === "down" ? "bg-red-100 text-red-700" :
                              "bg-gray-100 text-gray-700"
                            }`}>
                              {item.trend}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{item.competition}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {rankResults.recommendations?.length > 0 && (
                  <Card className="p-4">
                    <h4 className="font-semibold mb-2">Recommendations</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {rankResults.recommendations.map((rec, i) => (
                        <li key={i}>• {rec}</li>
                      ))}
                    </ul>
                  </Card>
                )}
              </div>
            )}
          </div>
        );

      case "tag-generator":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                <Hash className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">YouTube Tag Generator</h2>
                <p className="text-gray-600">Generate high-ranking tags for your videos</p>
              </div>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="Video title"
                value={videoTitle}
                onChange={(e) => setVideoTitle(e.target.value)}
                data-testid="yt-tag-title-input"
              />
              <Textarea
                placeholder="Video description (optional)"
                value={videoDescription}
                onChange={(e) => setVideoDescription(e.target.value)}
                rows={3}
                data-testid="yt-tag-description-input"
              />
              <Button 
                onClick={handleTagGenerator}
                disabled={loading || !videoTitle.trim()}
                className="w-full bg-violet-600 hover:bg-violet-700"
                data-testid="yt-tag-generate-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Hash className="w-4 h-4 mr-2" />}
                Generate Tags
              </Button>
            </div>

            {tagResults && (
              <div className="space-y-4">
                <Card className="p-4 bg-violet-50">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold">Generated Tags ({tagResults.total_tags})</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{tagResults.total_characters}/500 chars</span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyToClipboard(tagResults.copy_text)}
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tagResults.tags?.map((tag, i) => (
                      <span key={i} className="px-3 py-1 bg-white rounded-full text-sm border border-violet-200">
                        {tag}
                      </span>
                    ))}
                  </div>
                </Card>
              </div>
            )}
          </div>
        );

      case "title-optimizer":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Type className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">YouTube Title Optimizer</h2>
                <p className="text-gray-600">Analyze and improve your video titles for higher CTR</p>
              </div>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="Enter your current video title"
                value={titleToOptimize}
                onChange={(e) => setTitleToOptimize(e.target.value)}
                data-testid="yt-title-input"
              />
              <Input
                placeholder="Target keyword (optional)"
                value={targetKeyword}
                onChange={(e) => setTargetKeyword(e.target.value)}
                data-testid="yt-title-keyword-input"
              />
              <Button 
                onClick={handleTitleOptimizer}
                disabled={loading || !titleToOptimize.trim()}
                className="w-full bg-orange-600 hover:bg-orange-700"
                data-testid="yt-title-optimize-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
                Analyze Title
              </Button>
            </div>

            {titleResults && (
              <div className="space-y-4">
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">CTR Score</h3>
                    <div className="flex items-center gap-2">
                      <span className={`text-3xl font-bold ${
                        titleResults.analysis?.ctr_score >= 80 ? 'text-green-600' :
                        titleResults.analysis?.ctr_score >= 60 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {titleResults.analysis?.ctr_score}%
                      </span>
                      <span className={`text-2xl font-bold px-3 py-1 rounded-lg ${
                        titleResults.grade === 'A' || titleResults.grade === 'A+' ? 'bg-green-100 text-green-700' :
                        titleResults.grade === 'B' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {titleResults.grade}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{titleResults.analysis?.character_count}</p>
                      <p className="text-xs text-gray-500">Characters</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{titleResults.analysis?.word_count}</p>
                      <p className="text-xs text-gray-500">Words</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{titleResults.analysis?.has_numbers ? 'Yes' : 'No'}</p>
                      <p className="text-xs text-gray-500">Has Numbers</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{titleResults.analysis?.power_words_found?.length || 0}</p>
                      <p className="text-xs text-gray-500">Power Words</p>
                    </div>
                  </div>

                  {titleResults.analysis?.power_words_found?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">Power words found:</p>
                      <div className="flex flex-wrap gap-2">
                        {titleResults.analysis.power_words_found.map((word, i) => (
                          <span key={i} className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm">{word}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>

                {titleResults.optimized_alternatives?.length > 0 && (
                  <Card className="p-4">
                    <h3 className="font-semibold mb-3">Optimized Alternatives</h3>
                    <div className="space-y-3">
                      {titleResults.optimized_alternatives.map((alt, i) => (
                        <div key={i} className="p-3 bg-orange-50 rounded-lg">
                          <div className="flex justify-between items-start">
                            <p className="font-medium text-gray-900">{alt.title}</p>
                            <Button size="sm" variant="ghost" onClick={() => copyToClipboard(alt.title)}>
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{alt.reason}</p>
                          <p className="text-xs text-gray-400 mt-1">{alt.length} characters</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            )}
          </div>
        );

      case "ab-title-test":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">A/B Title Testing</h2>
                <p className="text-gray-600">Compare two titles to find the better performer</p>
              </div>
              <Badge className="ml-auto bg-gradient-to-r from-green-500 to-emerald-500 text-white">NEW</Badge>
            </div>

            {/* Input Form */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">A</span>
                  Title Option A
                </label>
                <Input
                  placeholder="e.g., 10 SEO Tips That Actually Work"
                  value={abTitleA}
                  onChange={(e) => setAbTitleA(e.target.value)}
                  className="text-lg"
                  data-testid="ab-title-a-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <span className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold">B</span>
                  Title Option B
                </label>
                <Input
                  placeholder="e.g., How I Ranked #1 on Google (SEO Secrets)"
                  value={abTitleB}
                  onChange={(e) => setAbTitleB(e.target.value)}
                  className="text-lg"
                  data-testid="ab-title-b-input"
                />
              </div>
            </div>

            <Button
              onClick={handleABTitleTest}
              disabled={loadingAB || !abTitleA.trim() || !abTitleB.trim()}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              data-testid="ab-test-btn"
            >
              {loadingAB ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
              ) : (
                <><Target className="w-4 h-4 mr-2" /> Compare Titles</>
              )}
            </Button>

            {/* Results */}
            {abResults && (
              <div className="space-y-6">
                {/* Winner Banner */}
                <div className={`p-6 rounded-xl text-center ${
                  abResults.winner === 'A' ? 'bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200' :
                  abResults.winner === 'B' ? 'bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200' :
                  'bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200'
                }`}>
                  <div className="text-4xl mb-2">
                    {abResults.winner === 'A' ? '🏆 A' : abResults.winner === 'B' ? '🏆 B' : '🤝'}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {abResults.winner === 'Tie' ? "It's a Tie!" : `Title ${abResults.winner} Wins!`}
                  </h3>
                  <p className="text-gray-600">{abResults.recommendation}</p>
                </div>

                {/* Detailed Comparison */}
                <div className="grid md:grid-cols-2 gap-6">
                  {['A', 'B'].map((option) => {
                    const data = option === 'A' ? abResults.titleA : abResults.titleB;
                    const isWinner = abResults.winner === option;
                    return (
                      <div key={option} className={`p-4 rounded-xl border-2 ${
                        isWinner ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'
                      }`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className={`w-8 h-8 ${option === 'A' ? 'bg-blue-500' : 'bg-purple-500'} text-white rounded-full flex items-center justify-center font-bold`}>
                            {option}
                          </span>
                          {isWinner && <Badge className="bg-green-500 text-white">Winner</Badge>}
                        </div>
                        <p className="font-medium text-gray-800 mb-4 line-clamp-2">{data.title}</p>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">CTR Score</span>
                            <span className="font-bold text-lg">{Math.round(data.score)}/100</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Est. CTR</span>
                            <span className="font-semibold text-green-600">{data.ctrEstimate}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Length</span>
                            <Badge className={data.length >= 40 && data.length <= 60 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                              {data.length} chars
                            </Badge>
                          </div>
                          <div className="flex gap-1 flex-wrap mt-2">
                            {data.hasNumbers && <Badge variant="outline" className="text-xs">Numbers ✓</Badge>}
                            {data.hasQuestion && <Badge variant="outline" className="text-xs">Question ✓</Badge>}
                            {data.hasPowerWords && <Badge variant="outline" className="text-xs">Power Words ✓</Badge>}
                            {data.hasEmoji && <Badge variant="outline" className="text-xs">Emoji ✓</Badge>}
                            {data.hasBrackets && <Badge variant="outline" className="text-xs">Brackets ✓</Badge>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!abResults && !loadingAB && (
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">Tips for Better Titles</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Keep titles between 40-60 characters</li>
                  <li>• Include numbers for higher CTR</li>
                  <li>• Use power words (Best, Secret, Ultimate)</li>
                  <li>• Add brackets [Tutorial] or (2024)</li>
                  <li>• Create curiosity with questions</li>
                </ul>
              </div>
            )}
          </div>
        );

      case "ctr-estimator":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">CTR Estimator</h2>
                <p className="text-gray-600">Estimate click-through rate from title 6 thumbnail</p>
              </div>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="Video title"
                value={ctrTitle}
                onChange={(e) => setCtrTitle(e.target.value)}
                data-testid="yt-ctr-title-input"
              />
              <Input
                placeholder="Thumbnail text (optional)"
                value={ctrThumbnailText}
                onChange={(e) => setCtrThumbnailText(e.target.value)}
                data-testid="yt-ctr-thumb-input"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  placeholder="Niche (optional)"
                  value={ctrNiche}
                  onChange={(e) => setCtrNiche(e.target.value)}
                  data-testid="yt-ctr-niche-input"
                />
                <Input
                  placeholder="Impressions (optional)"
                  type="number"
                  value={ctrImpressions}
                  onChange={(e) => setCtrImpressions(e.target.value)}
                  data-testid="yt-ctr-impressions-input"
                />
              </div>
              <Button
                onClick={handleCtrEstimator}
                disabled={loading || !ctrTitle.trim()}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="yt-ctr-estimate-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
                Estimate CTR
              </Button>
            </div>

            {ctrResults && (
              <div className="space-y-4">
                <Card className="p-4 bg-indigo-50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-gray-500">Estimated CTR Range</p>
                      <p className="text-3xl font-bold text-indigo-700" data-testid="yt-ctr-range">{ctrResults.estimated_ctr_range?.display}</p>
                    </div>
                    <span className={`text-2xl font-bold px-3 py-1 rounded-lg ${ctrResults.grade === "A" ? "bg-green-100 text-green-700" : ctrResults.grade === "B" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>
                      {ctrResults.grade}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500">Score</p>
                      <p className="font-semibold">{ctrResults.score}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Chars</p>
                      <p className="font-semibold">{ctrResults.analysis?.character_count}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Numbers</p>
                      <p className="font-semibold">{ctrResults.analysis?.has_numbers ? "Yes" : "No"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Power Words</p>
                      <p className="font-semibold">{ctrResults.analysis?.power_words_found?.length || 0}</p>
                    </div>
                  </div>
                </Card>

                {ctrResults.estimated_clicks && (
                  <Card className="p-4">
                    <h4 className="font-semibold mb-2">Estimated Clicks</h4>
                    <p className="text-sm text-gray-600" data-testid="yt-ctr-clicks">
                      {ctrResults.estimated_clicks.low.toLocaleString()} - {ctrResults.estimated_clicks.high.toLocaleString()} clicks
                    </p>
                  </Card>
                )}

                {ctrResults.suggestions?.length > 0 && (
                  <Card className="p-4">
                    <h4 className="font-semibold mb-2">Improvements</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {ctrResults.suggestions.map((tip, i) => (
                        <li key={i}>• {tip}</li>
                      ))}
                    </ul>
                  </Card>
                )}
              </div>
            )}
          </div>
        );

      case "description-generator":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">YouTube Description Generator</h2>
                <p className="text-gray-600">Create SEO-optimized descriptions with timestamps & CTAs</p>
              </div>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="Video title"
                value={descVideoTitle}
                onChange={(e) => setDescVideoTitle(e.target.value)}
                data-testid="yt-desc-title-input"
              />
              <Input
                placeholder="Target keywords (comma separated)"
                value={descKeywords}
                onChange={(e) => setDescKeywords(e.target.value)}
                data-testid="yt-desc-keywords-input"
              />
              <Button 
                onClick={handleDescriptionGenerator}
                disabled={loading || !descVideoTitle.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="yt-desc-generate-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                Generate Description
              </Button>
            </div>

            {descResults && (
              <div className="space-y-4">
                <Card className="p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold">Generated Description</h3>
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(descResults.description)}>
                      {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                      Copy
                    </Button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-sm">
                    {descResults.description}
                  </div>
                  <div className="flex gap-4 mt-3 text-sm text-gray-600">
                    <span>{descResults.metrics?.word_count} words</span>
                    <span>{descResults.metrics?.character_count} chars</span>
                    <span>{descResults.hashtags?.length} hashtags</span>
                  </div>
                </Card>
              </div>
            )}
          </div>
        );

      case "hashtag-generator":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">YouTube Hashtag Generator</h2>
                <p className="text-gray-600">Generate trending & niche-specific hashtags</p>
              </div>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="Enter your video topic"
                value={hashtagTopic}
                onChange={(e) => setHashtagTopic(e.target.value)}
                data-testid="yt-hashtag-topic-input"
              />
              <Input
                placeholder="Niche (optional, e.g., 'tech', 'gaming')"
                value={hashtagNiche}
                onChange={(e) => setHashtagNiche(e.target.value)}
                data-testid="yt-hashtag-niche-input"
              />
              <Button 
                onClick={handleHashtagGenerator}
                disabled={loading || !hashtagTopic.trim()}
                className="w-full bg-pink-600 hover:bg-pink-700"
                data-testid="yt-hashtag-generate-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                Generate Hashtags
              </Button>
            </div>

            {hashtagResults && (
              <div className="space-y-4">
                <Card className="p-4 bg-pink-50">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold">Generated Hashtags ({hashtagResults.total_count})</h3>
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(hashtagResults.copy_text)}>
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {hashtagResults.hashtags?.map((tag, i) => (
                      <span key={i} className="px-3 py-1 bg-white rounded-full text-sm border border-pink-200 text-pink-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">{hashtagResults.note}</p>
                </Card>
              </div>
            )}
          </div>
        );

      case "script-generator":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Video className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">YouTube Script Generator</h2>
                <p className="text-gray-600">Generate engaging video scripts with hooks</p>
              </div>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="Video topic (e.g., '10 Tips for Better Sleep')"
                value={scriptTopic}
                onChange={(e) => setScriptTopic(e.target.value)}
                data-testid="yt-script-topic-input"
              />
              <div className="grid grid-cols-2 gap-3">
                <select 
                  className="w-full px-3 py-2 border rounded-md"
                  value={scriptType}
                  onChange={(e) => setScriptType(e.target.value)}
                  data-testid="yt-script-type-select"
                >
                  <option value="short">Short Script (Shorts)</option>
                  <option value="long">Long Form Script</option>
                  <option value="hook">Hook Variations Only</option>
                </select>
                <Input
                  type="number"
                  placeholder="Duration (minutes)"
                  value={scriptDuration}
                  onChange={(e) => setScriptDuration(parseInt(e.target.value) || 10)}
                  min={1}
                  max={60}
                  data-testid="yt-script-duration-input"
                />
              </div>
              <Button 
                onClick={handleScriptGenerator}
                disabled={loading || !scriptTopic.trim()}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="yt-script-generate-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Generate Script
              </Button>
            </div>

            {scriptResults && (
              <Card className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h3 className="font-semibold">Generated Script</h3>
                    <p className="text-sm text-gray-500">
                      {scriptResults.metrics?.word_count} words • ~{scriptResults.metrics?.estimated_duration_minutes} min
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(scriptResults.script)}>
                    {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    Copy
                  </Button>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-sm max-h-96 overflow-y-auto">
                  {scriptResults.script}
                </div>
              </Card>
            )}
          </div>
        );

      case "hook-generator":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">YouTube Hook Generator</h2>
                <p className="text-gray-600">Create attention-grabbing intros for the first 10 seconds</p>
              </div>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="What's your video about?"
                value={hookTopic}
                onChange={(e) => setHookTopic(e.target.value)}
                data-testid="yt-hook-topic-input"
              />
              <Button 
                onClick={handleHookGenerator}
                disabled={loading || !hookTopic.trim()}
                className="w-full bg-yellow-600 hover:bg-yellow-700"
                data-testid="yt-hook-generate-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                Generate Hooks
              </Button>
            </div>

            {hookResults && (
              <div className="space-y-3">
                {hookResults.hooks?.map((hook, i) => (
                  <Card key={i} className="p-4">
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-yellow-600 bg-yellow-100 px-2 py-1 rounded">
                        {hook.type}
                      </span>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(hook.text)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="mt-2 text-gray-900 font-medium">"{hook.text}"</p>
                    <p className="text-sm text-gray-500 mt-2">{hook.explanation}</p>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case "shorts-ideas":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <Film className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">YouTube Shorts Ideas</h2>
                <p className="text-gray-600">Get viral Shorts concepts for your niche</p>
              </div>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="Enter your niche (e.g., 'fitness', 'cooking', 'tech')"
                value={shortsNiche}
                onChange={(e) => setShortsNiche(e.target.value)}
                data-testid="yt-shorts-niche-input"
              />
              <Button 
                onClick={handleShortsIdeas}
                disabled={loading || !shortsNiche.trim()}
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="yt-shorts-generate-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Film className="w-4 h-4 mr-2" />}
                Generate Ideas
              </Button>
            </div>

            {shortsResults && (
              <div className="space-y-3">
                {shortsResults.ideas?.map((idea, i) => (
                  <Card key={i} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start">
                      <h4 className="font-semibold text-gray-900">{idea.title}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        idea.viral_potential?.toLowerCase().includes('high') ? 'bg-green-100 text-green-700' :
                        idea.viral_potential?.toLowerCase().includes('medium') ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {idea.viral_potential}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{idea.concept}</p>
                    <div className="flex gap-3 mt-2 text-xs text-gray-500">
                      <span>Hook: {idea.hook}</span>
                      <span>Format: {idea.format}</span>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case "shorts-scripts":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Shorts Script Templates</h2>
                <p className="text-gray-600">Ready-made scripts for viral shorts</p>
              </div>
              <Badge className="ml-auto bg-gradient-to-r from-rose-500 to-pink-500 text-white">NEW</Badge>
            </div>

            <div className="flex gap-4">
              <Input placeholder="Enter your niche (e.g., Fitness, Tech, Cooking)" value={shortsScriptNiche} onChange={(e) => setShortsScriptNiche(e.target.value)} className="flex-1" data-testid="shorts-niche-input" />
              <Button onClick={handleGenerateShortsScripts} disabled={loadingShortsScripts} className="bg-gradient-to-r from-rose-500 to-pink-600" data-testid="generate-shorts-scripts-btn">
                {loadingShortsScripts ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-2" /> Generate</>}
              </Button>
            </div>

            {generatedShortsScripts && (
              <div className="grid md:grid-cols-2 gap-4">
                {generatedShortsScripts.scripts.map((script, i) => (
                  <Card key={i} className="p-4 hover:shadow-lg transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-bold text-gray-900">{script.title}</h4>
                      <Badge variant="outline">{script.duration}</Badge>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium text-rose-600">Hook:</span> <span className="text-gray-600">{script.hook}</span></div>
                      <div><span className="font-medium text-rose-600">Body:</span> <pre className="text-gray-600 whitespace-pre-wrap text-xs bg-gray-50 p-2 rounded mt-1">{script.body}</pre></div>
                      <div><span className="font-medium text-rose-600">CTA:</span> <span className="text-gray-600">{script.cta}</span></div>
                    </div>
                    <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => copyToClipboard(`${script.hook}\n\n${script.body}\n\n${script.cta}`)}>
                      <Copy className="w-3 h-3 mr-2" /> Copy Script
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case "video-calendar":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Video Idea Calendar</h2>
                <p className="text-gray-600">Monthly content ideas for your channel</p>
              </div>
              <Badge className="ml-auto bg-gradient-to-r from-teal-500 to-cyan-500 text-white">NEW</Badge>
            </div>

            <div className="flex gap-4">
              <Input placeholder="Enter your niche" value={calendarNiche} onChange={(e) => setCalendarNiche(e.target.value)} className="flex-1" data-testid="calendar-niche-input" />
              <Select value={calendarMonth.toString()} onValueChange={(v) => setCalendarMonth(parseInt(v))}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                    <SelectItem key={i} value={i.toString()}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleGenerateCalendar} disabled={loadingCalendar} className="bg-gradient-to-r from-teal-500 to-cyan-600" data-testid="generate-calendar-btn">
                {loadingCalendar ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Calendar className="w-4 h-4 mr-2" /> Generate</>}
              </Button>
            </div>

            {generatedCalendar && (
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-900">{generatedCalendar.month} Content Calendar</h3>
                  <Badge>{generatedCalendar.ideas.length} video ideas</Badge>
                </div>
                
                {[1, 2, 3, 4].map(week => (
                  <div key={week} className="bg-white rounded-xl border overflow-hidden">
                    <div className="p-3 bg-gray-50 font-semibold text-gray-700">Week {week}</div>
                    <div className="grid grid-cols-4 divide-x">
                      {generatedCalendar.ideas.filter(i => i.week === week).map((idea, i) => (
                        <div key={i} className="p-3 hover:bg-teal-50 transition-colors">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-medium text-gray-500">{idea.day}</span>
                            <Badge variant="outline" className="text-xs">{idea.type}</Badge>
                          </div>
                          <p className="text-sm font-medium text-gray-800 line-clamp-2">{idea.title}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "thumbnail-text":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Lightbulb className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Thumbnail Text Generator</h2>
                <p className="text-gray-600">Create CTR-boosting text for thumbnails</p>
              </div>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="Enter your video title"
                value={thumbVideoTitle}
                onChange={(e) => setThumbVideoTitle(e.target.value)}
                data-testid="yt-thumb-title-input"
              />
              <Button 
                onClick={handleThumbnailText}
                disabled={loading || !thumbVideoTitle.trim()}
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="yt-thumb-generate-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Lightbulb className="w-4 h-4 mr-2" />}
                Generate Text Options
              </Button>
            </div>

            {thumbResults && (
              <div className="space-y-3">
                {thumbResults.text_options?.map((opt, i) => (
                  <Card key={i} className="p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-bold text-gray-900">{opt.text}</span>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(opt.text)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex gap-3 mt-2 text-sm">
                      <span className="text-purple-600">Emotion: {opt.emotion}</span>
                      <span className="text-gray-500">{opt.design_tip}</span>
                    </div>
                  </Card>
                ))}

                <Card className="p-4 bg-purple-50">
                  <h4 className="font-semibold mb-2">Best Practices</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {thumbResults.best_practices?.map((tip, i) => (
                      <li key={i}>• {tip}</li>
                    ))}
                  </ul>
                </Card>
              </div>
            )}
          </div>
        );

      case "thumbnail-downloader":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <Download className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">YouTube Thumbnail Downloader</h2>
                <p className="text-gray-600">Download thumbnails in all available sizes</p>
              </div>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="Paste YouTube video URL (e.g., https://youtube.com/watch?v=xxxxx)"
                value={thumbnailUrl}
                onChange={(e) => setThumbnailUrl(e.target.value)}
                data-testid="yt-thumbnail-url-input"
              />
              <Button 
                onClick={handleThumbnailDownloader}
                disabled={!thumbnailUrl.trim()}
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="yt-thumbnail-fetch-btn"
              >
                <Image className="w-4 h-4 mr-2" />
                Get Thumbnails
              </Button>
            </div>

            {thumbnailResults?.error && (
              <Card className="p-4 bg-red-50 border-red-200">
                <p className="text-red-600">{thumbnailResults.error}</p>
              </Card>
            )}

            {thumbnailResults?.thumbnails && (
              <div className="space-y-4">
                {/* Preview */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-3">Preview (Max Resolution)</h3>
                  <div className="relative rounded-lg overflow-hidden bg-black">
                    <img 
                      src={thumbnailResults.thumbnails[0].url} 
                      alt="Video Thumbnail"
                      className="w-full h-auto"
                      onError={(e) => {
                        e.target.src = thumbnailResults.thumbnails[1].url;
                      }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Video ID: <code className="bg-gray-100 px-2 py-1 rounded">{thumbnailResults.videoId}</code>
                  </p>
                </Card>

                {/* Download Options */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Download Options</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {thumbnailResults.thumbnails.map((thumb, i) => (
                      <div 
                        key={i} 
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border hover:border-red-300 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <img 
                            src={thumb.url} 
                            alt={thumb.quality}
                            className="w-16 h-10 object-cover rounded"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <div>
                            <p className="font-medium text-sm">{thumb.quality}</p>
                            <p className="text-xs text-gray-500">{thumb.size}.jpg</p>
                          </div>
                        </div>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => downloadThumbnail(thumb.url, `thumbnail_${thumbnailResults.videoId}_${thumb.size}.jpg`)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Copy Links */}
                <Card className="p-4 bg-gray-50">
                  <h4 className="font-semibold mb-2">Direct Links</h4>
                  <div className="space-y-2">
                    {thumbnailResults.thumbnails.slice(0, 3).map((thumb, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input 
                          value={thumb.url} 
                          readOnly 
                          className="text-xs bg-white"
                        />
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => copyToClipboard(thumb.url)}
                        >
                          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}
          </div>
        );

      case "channel-audit":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <BarChart2 className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Channel Audit</h2>
                <p className="text-gray-600">Comprehensive analysis of your channel</p>
              </div>
            </div>
            <div className="space-y-3">
              <Input placeholder="Enter YouTube channel URL" value={channelUrl} onChange={(e) => setChannelUrl(e.target.value)} data-testid="yt-channel-url-input" />
              <Button onClick={handleChannelAudit} disabled={loading || !channelUrl.trim()} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="yt-channel-audit-btn">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <BarChart2 className="w-4 h-4 mr-2" />}
                Audit Channel
              </Button>
            </div>
            {channelAuditResults && (
              <Card className="p-4 bg-indigo-50">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-4xl font-bold text-indigo-600">{channelAuditResults.overall_score}/100</span>
                  <span className="text-3xl font-bold bg-indigo-100 px-3 py-1 rounded-lg">{channelAuditResults.grade}</span>
                </div>
                <h4 className="font-semibold mb-2">Priority Actions</h4>
                <ul className="text-sm space-y-1">{channelAuditResults.priority_actions?.map((a, i) => <li key={i}>• {a}</li>)}</ul>
              </Card>
            )}
          </div>
        );

      case "competitor-analyzer":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Competitor Analyzer</h2>
                <p className="text-gray-600">Find content gaps and opportunities</p>
              </div>
            </div>
            <div className="space-y-3">
              <Input placeholder="Your channel URL" value={myChannelUrl} onChange={(e) => setMyChannelUrl(e.target.value)} data-testid="yt-competitor-my-channel-input" />
              <Textarea placeholder="Competitor URLs (one per line)" value={competitorUrls} onChange={(e) => setCompetitorUrls(e.target.value)} rows={3} data-testid="yt-competitor-urls-input" />
              <Button onClick={handleCompetitorAnalysis} disabled={loading || !myChannelUrl.trim()} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="yt-competitor-analyze-btn">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
                Analyze
              </Button>
            </div>
            {competitorResults && (
              <div className="space-y-4">
                <Card className="p-4"><h4 className="font-semibold mb-2">Content Gaps</h4><div className="flex flex-wrap gap-2">{competitorResults.content_gaps?.map((g, i) => <span key={i} className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">{g}</span>)}</div></Card>
                <Card className="p-4 bg-green-50"><h4 className="font-semibold mb-2">Quick Wins</h4><ul className="text-sm space-y-1">{competitorResults.quick_wins?.map((w, i) => <li key={i}>✓ {w}</li>)}</ul></Card>
              </div>
            )}
          </div>
        );

      case "trending-topics":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Trending Topics</h2>
                <p className="text-gray-600">What's hot in your niche</p>
              </div>
            </div>
            <div className="space-y-3">
              <Input placeholder="Your niche (e.g., fitness, tech)" value={trendingNiche} onChange={(e) => setTrendingNiche(e.target.value)} data-testid="yt-trending-niche-input" />
              <Button onClick={handleTrendingTopics} disabled={loading || !trendingNiche.trim()} className="w-full bg-pink-600 hover:bg-pink-700" data-testid="yt-trending-search-btn">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                Find Trends
              </Button>
            </div>
            {trendingResults && (
              <div className="space-y-3">{trendingResults.trending_topics?.map((t, i) => (
                <Card key={i} className="p-3"><h4 className="font-semibold">{t.TOPIC || t.topic}</h4><p className="text-sm text-gray-600">{t.VIDEO_IDEA || t.video_idea}</p></Card>
              ))}</div>
            )}
          </div>
        );

      case "best-upload-time": {
        const bestTimesList = normalizeUploadTimes(uploadResults?.recommendations?.best_times);
        const avoidTimesList = normalizeUploadTimes(uploadResults?.recommendations?.avoid_times);
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Best Upload Time Finder</h2>
                <p className="text-gray-600">Find the best days & times for your audience</p>
              </div>
            </div>

            <div className="space-y-3">
              <Input
                placeholder="Target audience (e.g., Indian students, US SaaS founders)"
                value={uploadAudience}
                onChange={(e) => setUploadAudience(e.target.value)}
                data-testid="yt-upload-audience-input"
              />
              <Input
                placeholder="Timezone (e.g., Asia/Kolkata)"
                value={uploadTimezone}
                onChange={(e) => setUploadTimezone(e.target.value)}
                data-testid="yt-upload-timezone-input"
              />
              <Button
                onClick={handleBestUploadTime}
                disabled={loading || !uploadAudience.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="yt-upload-find-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
                Find Best Times
              </Button>
            </div>

            {uploadResults && (
              <div className="space-y-4">
                <Card className="p-4 bg-blue-50">
                  <h4 className="font-semibold mb-2">Best Days</h4>
                  <div className="flex flex-wrap gap-2" data-testid="yt-upload-best-days">
                    {uploadResults.recommendations?.best_days?.map((day, i) => (
                      <span key={i} className="px-3 py-1 bg-white rounded-full text-sm border">{day}</span>
                    ))}
                  </div>
                </Card>

                <Card className="p-4">
                  <h4 className="font-semibold mb-2">Best Times</h4>
                  <div className="flex flex-wrap gap-2" data-testid="yt-upload-best-times">
                    {bestTimesList.map((item, i) => (
                      <span key={`${item.label}-${i}`} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">{item.label}</span>
                    ))}
                  </div>
                </Card>

                <Card className="p-4">
                  <h4 className="font-semibold mb-2">Avoid Times</h4>
                  <div className="flex flex-wrap gap-2" data-testid="yt-upload-avoid-times">
                    {avoidTimesList.map((item, i) => (
                      <span key={`${item.label}-${i}`} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">{item.label}</span>
                    ))}
                  </div>
                </Card>

                <Card className="p-4 bg-gray-50">
                  <h4 className="font-semibold mb-2">Reasoning</h4>
                  <p className="text-sm text-gray-600" data-testid="yt-upload-reasoning">
                    {formatReasoning(uploadResults.recommendations?.reasoning)}
                  </p>
                </Card>
              </div>
            )}
          </div>
        );
      }

      case "monetization-checker":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Monetization Checker</h2>
                <p className="text-gray-600">YPP eligibility calculator</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input placeholder="Subscribers" type="number" value={subscribers} onChange={(e) => setSubscribers(e.target.value)} data-testid="yt-monetization-subs-input" />
              <Input placeholder="Watch Hours" type="number" value={watchHours} onChange={(e) => setWatchHours(e.target.value)} data-testid="yt-monetization-hours-input" />
              <Input placeholder="Shorts Views" type="number" value={shortsViews} onChange={(e) => setShortsViews(e.target.value)} data-testid="yt-monetization-shorts-input" />
            </div>
            <Button onClick={handleMonetizationCheck} disabled={loading || !subscribers || !watchHours} className="w-full bg-green-600 hover:bg-green-700" data-testid="yt-monetization-check-btn">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Clock className="w-4 h-4 mr-2" />}
              Check Eligibility
            </Button>
            {monetizationResults && (
              <Card className={`p-4 ${monetizationResults.eligibility?.any_path ? 'bg-green-100' : 'bg-yellow-100'}`}>
                <p className="text-xl font-bold text-center">{monetizationResults.eligibility?.any_path ? '🎉 Eligible!' : `⏳ ${monetizationResults.estimated_time?.total_days} days left`}</p>
                <div className="mt-3 space-y-2">
                  <div><span className="text-sm">Subs:</span> <span className="font-bold">{monetizationResults.progress?.subscribers?.percentage}%</span></div>
                  <div><span className="text-sm">Watch Hours:</span> <span className="font-bold">{monetizationResults.progress?.watch_hours?.percentage}%</span></div>
                </div>
              </Card>
            )}
          </div>
        );

      case "engagement-calculator":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                <BarChart2 className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Engagement Calculator</h2>
                <p className="text-gray-600">Analyze video performance</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input placeholder="Views" type="number" value={engViews} onChange={(e) => setEngViews(e.target.value)} data-testid="yt-engagement-views-input" />
              <Input placeholder="Likes" type="number" value={engLikes} onChange={(e) => setEngLikes(e.target.value)} data-testid="yt-engagement-likes-input" />
              <Input placeholder="Comments" type="number" value={engComments} onChange={(e) => setEngComments(e.target.value)} data-testid="yt-engagement-comments-input" />
            </div>
            <Button onClick={handleEngagementCalculator} disabled={loading || !engViews || !engLikes} className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="yt-engagement-calc-btn">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <BarChart2 className="w-4 h-4 mr-2" />}
              Calculate
            </Button>
            {engagementResults && (
              <Card className="p-4 bg-cyan-50 text-center">
                <p className="text-4xl font-bold text-cyan-600">{engagementResults.engagement_rate}%</p>
                <p className="text-sm text-gray-600">Engagement Rate</p>
                <span className={`inline-block mt-2 px-3 py-1 rounded-full font-bold ${engagementResults.grade === 'A+' || engagementResults.grade === 'A' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{engagementResults.grade}</span>
                <p className="text-sm mt-2">{engagementResults.verdict}</p>
              </Card>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm mb-4" aria-label="Breadcrumb">
          <RouterLink to="/" className="flex items-center gap-1 text-gray-500 hover:text-red-600 transition-colors">
            <Home className="w-4 h-4" />
            <span>Home</span>
          </RouterLink>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <RouterLink to="/tools" className="text-gray-500 hover:text-red-600 transition-colors">
            Tools
          </RouterLink>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="text-gray-900 font-medium">YouTube Tools</span>
        </nav>

        {/* Page Title — Premium */}
        <div className="mb-10">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#111111] rounded-2xl flex items-center justify-center">
              <Youtube className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs tracking-[0.18em] font-semibold text-[#A855F7] uppercase mb-1">YouTube Suite</p>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#111111]" data-testid="page-title">YouTube SEO Tools</h1>
              <p className="text-slate-600 mt-1.5">Optimize your YouTube channel and videos for maximum growth</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">YouTube Tools</h3>
              <div className="space-y-2">
                {tools.map((tool) => (
                  <button
                    key={tool.id}
                    onClick={() => setActiveTool(tool.id)}
                    data-testid={`yt-tool-${tool.id}`}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                      tool.highlight && activeTool !== tool.id
                        ? 'bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 text-red-700'
                        : activeTool === tool.id
                          ? 'bg-red-100 text-red-700 font-medium'
                          : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <tool.icon className={`w-5 h-5 ${tool.highlight && activeTool !== tool.id ? 'text-red-500' : ''}`} />
                      <div>
                        <span className="text-sm font-medium block">{tool.name}</span>
                        {(activeTool === tool.id || tool.highlight) && (
                          <p className={`text-xs mt-0.5 ${tool.highlight && activeTool !== tool.id ? 'text-orange-600' : 'text-red-500'}`}>{tool.desc}</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card className="p-6">
              {renderToolContent()}
            </Card>
          </div>
        </div>

        {/* Related Tools Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <RelatedTools currentToolId={activeTool} category="youtube" />
        </div>
        <ToolSEOContent toolId="youtube-tools" />
      </div>
      <Footer />
    </div>
  );
};

export default YouTubeSEOTools;
